import _ from "lodash";
import Promise from "bluebird";
import Backbone from "backbone";
import HybridPageableMediaCollection from "scripts/collections/hybridPageableMediaCollection";
import AnthologyCollectionContentCountModel from "scripts/models/anthologyCollectionContentCountModel";
import BaseView from "scripts/views/baseView";
import MediaListView from "scripts/views/mediaListView";
import MediaSalvattoreGridView from "scripts/views/mediaSalvattoreGridView";
import PagerView from "scripts/views/pagerView";
import StackedMediaTypesChartView from "scripts/views/stackedMediaTypesChartView";
import { redirectToFragment } from "scripts/utils/routerHelpers";

import templateAnthologyCollectionPageContentSubPage from "templates/anthologyCollectionPageContentSubPage.hbs";

const PAGE_SIZE = 100;

class AnthologyCollectionPageContentSubPageView extends BaseView {
  get template() {
    return templateAnthologyCollectionPageContentSubPage;
  }

  constructor(options) {
    super(options);

    this.mediaModel = options.mediaModel;

    this.anthologyCollectionContentCountModel = new AnthologyCollectionContentCountModel(
      {
        id: this.model.get("anthologyCollectionId"),
      },
    );

    const anthologyCollectionId = this.model.get("anthologyCollectionId");
    const contentTypeUrlFragment = this.contentTypeUrlFragment(
      this.model.get("contentType"),
    );

    const pageIndex = this.model.get("pageIndex");

    this.collection = new HybridPageableMediaCollection([], {
      path: `/collections/${anthologyCollectionId}/content${contentTypeUrlFragment}`,
      state: {
        currentPage: pageIndex,
        pageSize: PAGE_SIZE,
      },
    });

    /**
     * Hack to satisfy Backbone.PageableCollection._checkState
     * if starting from a page other than 0.
     */
    if (pageIndex > 0) {
      this.collection.links[pageIndex] = this.collection.url;
    }

    this.stackedMediaTypesChartView = this.addSubView(
      new StackedMediaTypesChartView({
        model: new Backbone.Model(),
      }),
    );
  }

  createContentView() {
    let subPage;

    let pageViewOptions = {
      hasPreviousPage: this.collection.hasPreviousPage(),
      hasNextPage: this.collection.hasNextPage(),
      pageNumber: this.model.get("pageIndex") + 1,
    };

    let presentationType = this.model.get("presentationType");

    if (presentationType === "list") {
      subPage = new PagerView(
        _.extend(
          {
            mediaView: new MediaListView({
              collection: this.collection,
            }),
          },
          pageViewOptions,
        ),
      );
    } else {
      subPage = new PagerView(
        _.extend(
          {
            mediaView: new MediaSalvattoreGridView({
              collection: this.collection,
            }),
          },
          pageViewOptions,
        ),
      );
    }

    subPage.on(
      "pageSequenceChanged",
      pageNumber => {
        this.model.set("pageIndex", pageNumber - 1);
        redirectToFragment(this.urlFragment());
      },
      this,
    );

    return subPage;
  }

  render() {
    this.$el.html(
      this.template(
        _.merge(
          {},
          this.model.toJSON(),
          this.anthologyCollectionContentCountModel.toJSON(),
          this.mediaModel.toJSON(),
        ),
      ),
    );

    this.stackedMediaTypesChartView.model.set(this.mediaTypePercentages());

    this.stackedMediaTypesChartView
      .appendTo(
        $(
          "#bb-anthology-collection-page-content-sub-page-stacked-media-types-chart-region",
        ),
      )
      .render();

    this.contentView
      .appendTo(
        $("#bb-anthology-collection-page-content-sub-page-content-region"),
      )
      .render();

    this.setActiveFilter();

    return this;
  }

  setActiveFilter() {
    const contentType = this.model.get("contentType");

    if (contentType === "all-content") {
      $(
        "#bb-anthology-collection-page-content-sub-page-all-content-link",
      ).addClass("active");
    } else {
      this.$el.find("a[href$=" + contentType + "].filter").addClass("active");
    }
  }

  percent(num, divisor) {
    return (num / divisor) * 100;
  }

  mediaTypePercentages() {
    let allContent = this.anthologyCollectionContentCountModel.get(
      "allContentCount",
    );

    let written = this.anthologyCollectionContentCountModel.get("articleCount");
    written += this.anthologyCollectionContentCountModel.get("bookCount");
    written += this.anthologyCollectionContentCountModel.get(
      "otherDocumentCount",
    );
    written += this.anthologyCollectionContentCountModel.get("chapterCount");

    let image = this.anthologyCollectionContentCountModel.get("imageCount");
    let audio = this.anthologyCollectionContentCountModel.get("audioCount");
    let video = this.anthologyCollectionContentCountModel.get("videoCount");

    return {
      writtenPercent: this.percent(written, allContent),
      imagePercent: this.percent(image, allContent),
      audioPercent: this.percent(audio, allContent),
      videoPercent: this.percent(video, allContent),
    };
  }

  sync() {
    return Promise.all([
      this.anthologyCollectionContentCountModel.fetch(),
      this.syncCollection().then(() => {
        this.contentView = this.addSubView(this.createContentView());
      }),
      this.mediaModel.fetch(),
    ]);
  }

  syncCollection() {
    this.collection.state.currentPage = this.model.get("pageIndex");

    this.collection.queryParams = {
      limit: PAGE_SIZE,
      offset:
        this.collection.state.currentPage * this.collection.state.pageSize,
    };

    return this.collection
      .fetch()
      .cancellable()
      .then(o => o);
  }

  contentTypeUrlFragment(contentType) {
    if (contentType === "highlights") {
      return "?collectionHighlight=1";
    } else if (contentType === "all-content") {
      return "";
    } else {
      return `/${contentType}`;
    }
  }

  urlFragment() {
    const anthologyId = this.model.get("anthologyId");
    const anthologyCollectionId = this.model.get("anthologyCollectionId");
    const presentationType = this.model.get("presentationType");
    const contentType = this.model.get("contentType");
    const pageNumber = this.model.get("pageIndex") + 1;

    return `anthology-collection/${anthologyId}/${anthologyCollectionId}/${presentationType}/${contentType}/${pageNumber}`;
  }
}

export default AnthologyCollectionPageContentSubPageView;
